import type { Error as ErrorEnvelope } from '@/lib/api/generated/model';

/**
 * The refusals the execution-images endpoints can answer with, one class
 * each — same reasoning `features/connections/errors.ts` applies to its own
 * error set.
 *
 * Backend codes, per `product-x-next-service`'s
 * `app/features/execution_images/errors.py`: `EXECUTION_IMAGE_NOT_FOUND`
 * (404), `EXECUTION_IMAGE_INVALID_REGISTRY_CONNECTION` (422),
 * `EXECUTION_IMAGE_CANNOT_DELETE_DEFAULT` (422). No code exists for a
 * duplicate image reference — that case is fully prevented client-side
 * (research.md D3) and never reaches the server in the golden path.
 */

/** Which part of the form a refusal belongs to. */
export type ExecutionImageErrorField = 'connectionId' | 'form';

class ExecutionImageError extends Error {
  readonly status: number;
  readonly field: ExecutionImageErrorField;

  constructor(
    name: string,
    status: number,
    field: ExecutionImageErrorField,
    message: string,
  ) {
    super(message);
    this.name = name;
    this.status = status;
    this.field = field;
  }
}

/** No execution image with that id exists in this workspace. */
export class ExecutionImageNotFoundError extends ExecutionImageError {
  constructor(message?: string) {
    super(
      'ExecutionImageNotFoundError',
      404,
      'form',
      message ?? 'That execution image no longer exists.',
    );
  }
}

/** `connection_id` is missing, in another workspace, or not `oci_registry`-typed. */
export class InvalidRegistryConnectionError extends ExecutionImageError {
  constructor(message?: string) {
    super(
      'InvalidRegistryConnectionError',
      422,
      'connectionId',
      message ?? 'Choose a connected registry to source this image from.',
    );
  }
}

/** The target is the workspace's current default and cannot be deleted. */
export class CannotDeleteDefaultExecutionImageError extends ExecutionImageError {
  constructor(message?: string) {
    super(
      'CannotDeleteDefaultExecutionImageError',
      422,
      'form',
      message ??
        'This is the current default image. Make a different image default first.',
    );
  }
}

/**
 * A refusal this build does not recognise — a rule the service grew later.
 * Deliberately vague: claiming a cause it cannot know would be worse than
 * admitting it does not know one.
 */
export class ExecutionImageRequestFailedError extends ExecutionImageError {
  constructor(status: number, message?: string) {
    super(
      'ExecutionImageRequestFailedError',
      status,
      'form',
      message ?? 'The request failed. Please try again.',
    );
  }
}

const BY_CODE: Record<string, (message?: string) => ExecutionImageError> = {
  EXECUTION_IMAGE_NOT_FOUND: (m) => new ExecutionImageNotFoundError(m),
  EXECUTION_IMAGE_INVALID_REGISTRY_CONNECTION: (m) =>
    new InvalidRegistryConnectionError(m),
  EXECUTION_IMAGE_CANNOT_DELETE_DEFAULT: (m) =>
    new CannotDeleteDefaultExecutionImageError(m),
};

/**
 * Map one error response to its typed refusal.
 *
 * The service's own `message` is deliberately **not** passed through, for
 * the same reason `toConnectionError` doesn't: the classes carry messages
 * written for the person looking at the form, not for a log reader. The
 * envelope's `error` code is what this reads.
 */
export function toExecutionImageError(status: number, body: unknown): Error {
  const code =
    typeof body === 'object' && body !== null && 'error' in body
      ? (body as ErrorEnvelope).error
      : undefined;

  const construct = code ? BY_CODE[code] : undefined;
  return construct ? construct() : new ExecutionImageRequestFailedError(status);
}

/** Narrows a mutation's `unknown` error to a typed refusal (constitution §2.5). */
export function isExecutionImageError(
  error: unknown,
): error is ExecutionImageError {
  return error instanceof ExecutionImageError;
}

export type { ExecutionImageError };
